import { 
  Document, 
  Packer, 
  Paragraph, 
  TextRun, 
  HeadingLevel, 
  Table, 
  TableRow, 
  TableCell,
  WidthType,
  AlignmentType,
  UnderlineType,
  BorderStyle
} from 'docx';

class WordDocumentService {
  constructor() {
    this.document = null;
  }

  // Create document with AI analysis results
  async createAnalysisDocument(analysisResults, exportMetadata = {}) {
    try {
      const children = [];

      // Document title and header
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: "AWS Health Assessment Report",
              bold: true,
              size: 32,
              color: "2F5496"
            })
          ],
          heading: HeadingLevel.TITLE,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 }
        })
      );

      // Executive summary
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: "Analysis Summary",
              bold: true,
              size: 24,
              color: "2F5496"
            })
          ],
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 400, after: 200 }
        })
      );

      // Analysis metadata
      const analysisDate = new Date(analysisResults.analysisDate).toLocaleDateString();
      
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `Analysis Date: ${analysisDate} | Services Analyzed: ${analysisResults.summary.totalServicesAnalyzed}`,
              italics: true,
              size: 20
            })
          ],
          spacing: { after: 200 }
        })
      );

      // Overall summary
      const overallSummary = this.generateOverallSummary(analysisResults);
      children.push(
        new Paragraph({
          children: [new TextRun({ text: overallSummary, size: 22 })],
          spacing: { after: 400 }
        })
      );

      // Service-specific sections
      for (const [serviceName, serviceData] of Object.entries(analysisResults.services)) {
        const serviceSection = await this.createServiceSection(serviceName, serviceData);
        children.push(...serviceSection);
      }

      // Error summary if any
      if (analysisResults.summary.errors.length > 0) {
        children.push(...this.createErrorSection(analysisResults.summary.errors));
      }

      // No footer needed

      this.document = new Document({
        sections: [{
          properties: {},
          children: children
        }]
      });

      return this.document;

    } catch (error) {
      console.error('Error creating Word document:', error);
      throw new Error(`Failed to create Word document: ${error.message}`);
    }
  }

  // Generate overall summary from all services
  generateOverallSummary(analysisResults) {
    const summaryParts = [];
    let highPriorityRecommendations = 0;
    const regions = new Set();

    for (const [serviceName, serviceData] of Object.entries(analysisResults.services)) {
      const analysis = serviceData.analysis;
      const data = serviceData.data;
      
      // Collect regions from the data
      if (data.summary && data.summary.regions) {
        data.summary.regions.forEach(region => regions.add(region));
      }
      
      if (analysis && analysis.recommendations) {
        highPriorityRecommendations += analysis.recommendations.filter(r => r.priority === 'high').length;
      }

      // Service-specific summary
      if (analysis && analysis.executiveSummary) {
        summaryParts.push(`${this.getServiceDisplayName(serviceName)}: ${analysis.executiveSummary}`);
      } else if (serviceName === 'trustedAdvisor' && data.summary) {
        // Special case for Trusted Advisor manual summarization with specific priority counts
        const taData = data.summary;
        const highPriorityCount = taData.highPriorityActions || 0;
        const mediumPriorityCount = taData.mediumPriorityActions || 0;
        
        let taSummary = `Identified ${highPriorityCount} high-priority and ${mediumPriorityCount} medium-priority recommendations requiring attention`;
        if (taData.potentialMonthlySavings.contributingRecommendations > 0) {
          taSummary += ` with potential monthly savings of ${taData.potentialMonthlySavings.formattedAmount}`;
        }
        summaryParts.push(`${this.getServiceDisplayName(serviceName)}: ${taSummary}.`);
      }
    }

    const regionsList = Array.from(regions).join(', ');
    const regionText = regions.size > 0 ? ` Analysis performed in region(s): ${regionsList}.` : '';
    
    let overallSummary = `This analysis covered ${Object.keys(analysisResults.services).length} AWS services, with ${highPriorityRecommendations} high-priority recommendations for immediate attention.${regionText}`;
    
    if (summaryParts.length > 0) {
      overallSummary += '\n\n' + summaryParts.join('\n\n');
    }

    return overallSummary;
  }

  // Create service-specific section
  async createServiceSection(serviceName, serviceData) {
    const children = [];
    const analysis = serviceData.analysis;

    // Service header
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: this.getServiceDisplayName(serviceName),
            bold: true,
            size: 24,
            color: "2F5496"
          })
        ],
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 600, after: 200 },
        pageBreakBefore: true
      })
    );

    // Trusted Advisor-specific sections (manual summarization, no AI analysis)
    if (serviceName === 'trustedAdvisor') {
      children.push(...this.createTrustedAdvisorSection(serviceData.data));
      return children;
    }

    // Service overview
    if (analysis.metrics) {
      children.push(...this.createMetricsSection(analysis.metrics, serviceName));
    }

    // Executive summary for this service
    if (analysis.executiveSummary) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: "Service Summary",
              bold: true,
              size: 20,
              underline: { type: UnderlineType.SINGLE }
            })
          ],
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 200, after: 100 }
        })
      );

      children.push(
        new Paragraph({
          children: [new TextRun({ text: analysis.executiveSummary, size: 22 })],
          spacing: { after: 300 }
        })
      );
    }

    // Lambda-specific sections
    if (serviceName === 'lambda') {
      // Runtime deprecation analysis
      if (analysis.runtimeDeprecationAnalysis) {
        children.push(...this.createRuntimeDeprecationSection(analysis.runtimeDeprecationAnalysis));
      }

      // Log retention analysis  
      if (analysis.logRetentionAnalysis) {
        children.push(...this.createLogRetentionSection(analysis.logRetentionAnalysis));
      }

      // Unused functions analysis
      if (analysis.unusedFunctionsAnalysis) {
        children.push(...this.createUnusedFunctionsSection(analysis.unusedFunctionsAnalysis));
      }

      // Error rate analysis
      if (analysis.errorRateAnalysis) {
        children.push(...this.createErrorRateSection(analysis.errorRateAnalysis));
      }
    } else {
      // Keep existing structure for other services
      // Critical findings
      if (analysis.criticalFindings && analysis.criticalFindings.length > 0) {
        children.push(...this.createCriticalFindingsSection(analysis.criticalFindings));
      }

      // Optimization opportunities
      if (analysis.optimizationOpportunities && analysis.optimizationOpportunities.length > 0) {
        children.push(...this.createOptimizationSection(analysis.optimizationOpportunities));
      }
    }

    // Lex V2-specific sections
    if (serviceName === 'lexV2') {
      // Unused bots analysis
      if (analysis.unusedBotsAnalysis) {
        children.push(...this.createUnusedBotsSection(analysis.unusedBotsAnalysis));
      }

      // Conversation performance analysis
      if (analysis.conversationPerformanceAnalysis) {
        children.push(...this.createConversationPerformanceSection(analysis.conversationPerformanceAnalysis));
      }

      // Utterance performance analysis
      if (analysis.utterancePerformanceAnalysis) {
        children.push(...this.createUtterancePerformanceSection(analysis.utterancePerformanceAnalysis));
      }
    }

    // Connect-specific sections
    if (serviceName === 'connect') {
      // Contact Flow analysis per instance
      children.push(...this.createConnectAnalysisSection(serviceData.data));
    }

    // Recommendations
    if (analysis.recommendations && analysis.recommendations.length > 0) {
      children.push(...this.createRecommendationsSection(analysis.recommendations));
    }

    return children;
  }

  // Create metrics overview section
  createMetricsSection(metrics, serviceName) {
    const children = [];

    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "Key Metrics",
            bold: true,
            size: 20,
            underline: { type: UnderlineType.SINGLE }
          })
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 200, after: 100 }
      })
    );

    // Create metrics table
    const rows = [];
    
    // Header row
    rows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({
              children: [new TextRun({ text: "Metric", bold: true })]
            })],
            width: { size: 40, type: WidthType.PERCENTAGE }
          }),
          new TableCell({
            children: [new Paragraph({
              children: [new TextRun({ text: "Value", bold: true })]
            })],
            width: { size: 60, type: WidthType.PERCENTAGE }
          })
        ]
      })
    );

    // Data rows
    for (const [key, value] of Object.entries(metrics)) {
      rows.push(
        new TableRow({
          children: [
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ text: this.formatMetricKey(key) })]
              })]
            }),
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ text: String(value) })]
              })]
            })
          ]
        })
      );
    }

    children.push(
      new Table({
        rows: rows,
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: {
          top: { style: BorderStyle.SINGLE, size: 1 },
          bottom: { style: BorderStyle.SINGLE, size: 1 },
          left: { style: BorderStyle.SINGLE, size: 1 },
          right: { style: BorderStyle.SINGLE, size: 1 },
          insideHorizontal: { style: BorderStyle.SINGLE, size: 1 },
          insideVertical: { style: BorderStyle.SINGLE, size: 1 }
        }
      })
    );

    children.push(new Paragraph({ children: [], spacing: { after: 300 } }));

    return children;
  }

  // Create critical findings section
  createCriticalFindingsSection(findings) {
    const children = [];

    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "Critical Findings",
            bold: true,
            size: 20,
            underline: { type: UnderlineType.SINGLE },
            color: "C55A11"
          })
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 200, after: 100 }
      })
    );

    findings.forEach((finding, index) => {
      const severityColor = finding.severity === 'high' ? 'C5504B' : 
                           finding.severity === 'medium' ? 'C55A11' : '70AD47';

      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `${index + 1}. ${finding.finding}`,
              bold: true,
              size: 22
            }),
            new TextRun({
              text: ` [${finding.severity.toUpperCase()}]`,
              bold: true,
              color: severityColor,
              size: 20
            })
          ],
          spacing: { before: 200, after: 100 }
        })
      );

      if (finding.dataSupport) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Data Support: ", bold: true }),
              new TextRun({ text: finding.dataSupport })
            ],
            spacing: { after: 100 }
          })
        );
      }

      if (finding.functions && finding.functions.length > 0) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Affected Resources: ", bold: true }),
              new TextRun({ text: finding.functions.join(', '), italics: true })
            ],
            spacing: { after: 200 }
          })
        );
      }
    });

    return children;
  }

  // Create optimization opportunities section
  createOptimizationSection(opportunities) {
    const children = [];

    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "Optimization Opportunities",
            bold: true,
            size: 20,
            underline: { type: UnderlineType.SINGLE },
            color: "70AD47"
          })
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 200, after: 100 }
      })
    );

    opportunities.forEach((opportunity, index) => {
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `${index + 1}. ${opportunity.opportunity}`,
              bold: true,
              size: 22
            })
          ],
          spacing: { before: 200, after: 100 }
        })
      );

      if (opportunity.dataJustification) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Data Justification: ", bold: true }),
              new TextRun({ text: opportunity.dataJustification })
            ],
            spacing: { after: 100 }
          })
        );
      }

      if (opportunity.functions && opportunity.functions.length > 0) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Affected Functions: ", bold: true }),
              new TextRun({ text: opportunity.functions.join(', '), italics: true })
            ],
            spacing: { after: 200 }
          })
        );
      }
    });

    return children;
  }

  // Create runtime status section
  createRuntimeStatusSection(runtimeItems) {
    const children = [];

    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "Runtime Status Analysis",
            bold: true,
            size: 20,
            underline: { type: UnderlineType.SINGLE },
            color: "5B9BD5"
          })
        ],
        spacing: { before: 200, after: 100 }
      })
    );

    runtimeItems.forEach((item, index) => {
      const statusColor = item.status === 'current' ? '70AD47' : 
                         item.status === 'deprecated' ? 'C55A11' : 'C5504B';

      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `${item.area}`,
              bold: true,
              size: 22
            }),
            new TextRun({
              text: ` [${item.status.toUpperCase()}]`,
              bold: true,
              color: statusColor,
              size: 20
            })
          ],
          spacing: { before: 200, after: 100 }
        })
      );

      if (item.details) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: item.details })],
            spacing: { after: 100 }
          })
        );
      }

      if (item.affectedFunctions && item.affectedFunctions.length > 0) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Affected Functions: ", bold: true }),
              new TextRun({ text: item.affectedFunctions.join(', '), italics: true })
            ],
            spacing: { after: 200 }
          })
        );
      }
    });

    return children;
  }




  // Create runtime deprecation section (Lambda)
  createRuntimeDeprecationSection(runtimeData) {
    const children = [];

    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "Runtime Deprecation Analysis",
            bold: true,
            size: 20,
            underline: { type: UnderlineType.SINGLE },
            color: "C5504B"
          })
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 200, after: 100 }
      })
    );

    children.push(
      new Paragraph({
        children: [new TextRun({ text: runtimeData.summary, size: 22 })],
        spacing: { after: 200 }
      })
    );

    // Handle new summary-based data structure
    if (runtimeData.totalDeprecatedFunctions) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `Total Deprecated Functions: ${runtimeData.totalDeprecatedFunctions}`,
              bold: true,
              size: 22,
              color: "C5504B"
            })
          ],
          spacing: { before: 100, after: 100 }
        })
      );
    }

    // Handle deprecation breakdown if available
    if (runtimeData.deprecationBreakdown) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: "Runtime Deprecation Breakdown:", bold: true, size: 18 })
          ],
          spacing: { before: 150, after: 100 }
        })
      );

      // If it's a detailed breakdown, parse and display it properly
      if (typeof runtimeData.deprecationBreakdown === 'string') {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: runtimeData.deprecationBreakdown, size: 20 })
            ],
            spacing: { after: 150 }
          })
        );
      } else if (typeof runtimeData.deprecationBreakdown === 'object') {
        // Display each runtime deprecation type with counts
        Object.entries(runtimeData.deprecationBreakdown).forEach(([runtime, count]) => {
          children.push(
            new Paragraph({
              children: [
                new TextRun({ text: `• ${runtime}: `, bold: true }),
                new TextRun({ text: `${count} functions`, color: "C5504B" })
              ],
              spacing: { after: 50 }
            })
          );
        });
        children.push(new Paragraph({ children: [], spacing: { after: 100 } }));
      }
    }

    return children;
  }

  // Create log retention section (Lambda)
  createLogRetentionSection(logData) {
    const children = [];

    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "Log Retention Analysis",
            bold: true,
            size: 20,
            underline: { type: UnderlineType.SINGLE },
            color: "5B9BD5"
          })
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 200, after: 100 }
      })
    );

    children.push(
      new Paragraph({
        children: [new TextRun({ text: logData.summary, size: 22 })],
        spacing: { after: 100 }
      })
    );

    if (logData.findings && logData.findings.length > 0) {
      logData.findings.forEach((finding, index) => {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: `• ${finding}` })
            ],
            spacing: { after: 50 }
          })
        );
      });
    }

    return children;
  }

  // Create unused functions section (Lambda)
  createUnusedFunctionsSection(unusedData) {
    const children = [];

    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "Unused Functions Analysis",
            bold: true,
            size: 20,
            underline: { type: UnderlineType.SINGLE },
            color: "C55A11"
          })
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 200, after: 100 }
      })
    );

    children.push(
      new Paragraph({
        children: [new TextRun({ text: unusedData.summary, size: 22 })],
        spacing: { after: 150 }
      })
    );

    // Handle summary-based unused functions data
    if (unusedData.totalUnusedFunctions) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `Total Unused Functions: ${unusedData.totalUnusedFunctions}`,
              bold: true,
              size: 20
            })
          ],
          spacing: { after: 100 }
        })
      );
    }
    
    if (unusedData.percentageOfTotal) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: "Percentage of Total: ", bold: true }),
            new TextRun({ text: `${unusedData.percentageOfTotal}%`, italics: true })
          ],
          spacing: { after: 50 }
        })
      );
    }

    return children;
  }

  // Create error rate section (Lambda)
  createErrorRateSection(errorData) {
    const children = [];

    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "Error Rate Analysis",
            bold: true,
            size: 20,
            underline: { type: UnderlineType.SINGLE },
            color: "C5504B"
          })
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 200, after: 100 }
      })
    );

    children.push(
      new Paragraph({
        children: [new TextRun({ text: errorData.summary, size: 22 })],
        spacing: { after: 150 }
      })
    );

    // Handle summary-based error rate data
    if (errorData.totalFunctionsWithErrors) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `Total Functions with Errors: ${errorData.totalFunctionsWithErrors}`,
              bold: true,
              size: 20
            })
          ],
          spacing: { after: 100 }
        })
      );
    }
    
    if (errorData.errorRateDistribution) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: "Error Rate Distribution: ", bold: true }),
            new TextRun({ text: errorData.errorRateDistribution, color: "C5504B" })
          ],
          spacing: { after: 50 }
        })
      );
    }

    return children;
  }

  // Create unused bots section (Lex)
  createUnusedBotsSection(unusedData) {
    const children = [];

    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "Unused Bots Analysis",
            bold: true,
            size: 20,
            underline: { type: UnderlineType.SINGLE },
            color: "C55A11"
          })
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 200, after: 100 }
      })
    );

    children.push(
      new Paragraph({
        children: [new TextRun({ text: unusedData.summary, size: 22 })],
        spacing: { after: 150 }
      })
    );



    return children;
  }


  // Create conversation performance section (Lex)
  createConversationPerformanceSection(performanceData) {
    const children = [];

    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "Conversation Performance Analysis",
            bold: true,
            size: 20,
            underline: { type: UnderlineType.SINGLE },
            color: "C5504B"
          })
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 200, after: 100 }
      })
    );

    children.push(
      new Paragraph({
        children: [new TextRun({ text: performanceData.summary, size: 22 })],
        spacing: { after: 150 }
      })
    );

    if (performanceData.botsWithIssues && performanceData.botsWithIssues.length > 0) {
      performanceData.botsWithIssues.forEach(bot => {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: bot.botName,
                bold: true
              }),
              new TextRun({
                text: ` - Failure Rate: ${bot.failureRate}% (${bot.failedConversations}/${bot.totalConversations})`,
                color: "C5504B"
              })
            ],
            spacing: { after: 50 }
          })
        );
      });
    }

    return children;
  }

  // Create utterance performance section (Lex)
  createUtterancePerformanceSection(utteranceData) {
    const children = [];

    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "Utterance Performance Analysis",
            bold: true,
            size: 20,
            underline: { type: UnderlineType.SINGLE },
            color: "C55A11"
          })
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 200, after: 100 }
      })
    );

    children.push(
      new Paragraph({
        children: [new TextRun({ text: utteranceData.summary, size: 22 })],
        spacing: { after: 150 }
      })
    );

    if (utteranceData.botsWithIssues && utteranceData.botsWithIssues.length > 0) {
      utteranceData.botsWithIssues.forEach(bot => {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: bot.botName,
                bold: true
              }),
              new TextRun({
                text: ` - Missed Utterance Rate: ${bot.missedUtteranceRate}% (${bot.missedUtterances}/${bot.detectedUtterances + bot.missedUtterances})`,
                color: "C55A11"
              })
            ],
            spacing: { after: 50 }
          })
        );
      });
    }

    return children;
  }

  createConnectAnalysisSection(connectData) {
    const children = [];

    // Add main section header
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "Contact Flow Analysis",
            bold: true,
            size: 20,
            underline: { type: UnderlineType.SINGLE }
          })
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 200, after: 100 }
      })
    );

    // Add analysis for each Connect instance
    if (connectData.instances && connectData.instances.length > 0) {
      connectData.instances.forEach(instance => {
        // Instance sub-heading
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `${instance.instanceName}`,
                bold: true,
                size: 18,
                color: "2F5496"
              })
            ],
            heading: HeadingLevel.HEADING_3,
            spacing: { before: 150, after: 75 }
          })
        );

        // Flow analysis content
        if (instance.flowsWithErrors === 0) {
          // No errors scenario
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `Total Contact Flows: ${instance.totalFlows}`,
                  size: 22
                })
              ],
              spacing: { after: 100 }
            })
          );

          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: "No flows have errors with no action items needed.",
                  size: 22,
                  color: "5D8A3A", // Green color for positive message
                  italic: true
                })
              ],
              spacing: { after: 200 }
            })
          );
        } else {
          // Has errors scenario
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `Total Contact Flows: ${instance.totalFlows}`,
                  size: 22
                })
              ],
              spacing: { after: 75 }
            })
          );

          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `Flows with Errors: ${instance.flowsWithErrors}`,
                  size: 22,
                  bold: true,
                  color: "C5504B" // Red color for errors
                })
              ],
              spacing: { after: 100 }
            })
          );

          // Add recommendation for error flows
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: "Recommendation: ",
                  size: 22,
                  bold: true
                }),
                new TextRun({
                  text: "Review and resolve contact flow errors to improve customer experience and system reliability. Detailed flow information can be found in the Excel export.",
                  size: 22
                })
              ],
              spacing: { after: 200 }
            })
          );
        }
      });

      // Add summary if multiple instances
      if (connectData.instances.length > 1) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "Overall Summary",
                bold: true,
                size: 18,
                underline: { type: UnderlineType.SINGLE }
              })
            ],
            heading: HeadingLevel.HEADING_3,
            spacing: { before: 200, after: 100 }
          })
        );

        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `Total Instances: ${connectData.summary.totalInstances}`,
                size: 22
              })
            ],
            spacing: { after: 75 }
          })
        );

        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `Total Contact Flows: ${connectData.summary.totalFlows}`,
                size: 22
              })
            ],
            spacing: { after: 75 }
          })
        );

        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `Total Flows with Errors: ${connectData.summary.totalFlowsWithErrors}`,
                size: 22,
                bold: connectData.summary.totalFlowsWithErrors > 0,
                color: connectData.summary.totalFlowsWithErrors > 0 ? "C5504B" : "000000"
              })
            ],
            spacing: { after: 200 }
          })
        );
      }
    } else {
      // No instances found
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: "No Amazon Connect instances with contact flows were found for analysis.",
              size: 22,
              italic: true
            })
          ],
          spacing: { after: 200 }
        })
      );
    }

    return children;
  }

  createTrustedAdvisorSection(serviceData) {
    const children = [];

    // Add main section header
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "AWS Trusted Advisor Analysis",
            bold: true,
            size: 20,
            underline: { type: UnderlineType.SINGLE }
          })
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 200, after: 100 }
      })
    );

    const summary = serviceData.summary;

    // Create metrics table
    const rows = [];
    
    // Header row
    rows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({
              children: [new TextRun({ text: "Metric", bold: true })]
            })],
            width: { size: 40, type: WidthType.PERCENTAGE }
          }),
          new TableCell({
            children: [new Paragraph({
              children: [new TextRun({ text: "Value", bold: true })]
            })],
            width: { size: 60, type: WidthType.PERCENTAGE }
          })
        ]
      })
    );

    // Data rows
    rows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({
              children: [new TextRun({ text: "Total Recommendations" })]
            })]
          }),
          new TableCell({
            children: [new Paragraph({
              children: [new TextRun({ text: String(summary.totalRecommendations) })]
            })]
          })
        ]
      })
    );

    rows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({
              children: [new TextRun({ text: "Action Recommended" })]
            })]
          }),
          new TableCell({
            children: [new Paragraph({
              children: [new TextRun({ text: String(summary.statusBreakdown.actionRecommended), color: summary.statusBreakdown.actionRecommended > 0 ? "C5504B" : "000000" })]
            })]
          })
        ]
      })
    );

    rows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({
              children: [new TextRun({ text: "Investigation Recommended" })]
            })]
          }),
          new TableCell({
            children: [new Paragraph({
              children: [new TextRun({ text: String(summary.statusBreakdown.investigationRecommended), color: summary.statusBreakdown.investigationRecommended > 0 ? "C55A11" : "000000" })]
            })]
          })
        ]
      })
    );

    if (summary.potentialMonthlySavings.contributingRecommendations > 0) {
      rows.push(
        new TableRow({
          children: [
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ text: "Potential Monthly Savings" })]
              })]
            }),
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ 
                  text: summary.potentialMonthlySavings.formattedAmount,
                  bold: true,
                  color: "70AD47"
                })]
              })]
            })
          ]
        })
      );

      rows.push(
        new TableRow({
          children: [
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ text: "Cost Optimization Recommendations" })]
              })]
            }),
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ text: String(summary.potentialMonthlySavings.contributingRecommendations) })]
              })]
            })
          ]
        })
      );
    }

    children.push(
      new Table({
        rows: rows,
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: {
          top: { style: BorderStyle.SINGLE, size: 1 },
          bottom: { style: BorderStyle.SINGLE, size: 1 },
          left: { style: BorderStyle.SINGLE, size: 1 },
          right: { style: BorderStyle.SINGLE, size: 1 },
          insideHorizontal: { style: BorderStyle.SINGLE, size: 1 },
          insideVertical: { style: BorderStyle.SINGLE, size: 1 }
        }
      })
    );

    children.push(new Paragraph({ children: [], spacing: { after: 300 } }));

    // Service Summary
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "Service Summary",
            bold: true,
            size: 18,
            underline: { type: UnderlineType.SINGLE }
          })
        ],
        heading: HeadingLevel.HEADING_3,
        spacing: { before: 200, after: 100 }
      })
    );

    // Generate summary text
    let summaryText = `AWS Trusted Advisor has analyzed ${summary.totalRecommendations} recommendations across various categories. `;
    
    if (summary.highPriorityActions > 0 || summary.mediumPriorityActions > 0) {
      summaryText += `There are ${summary.highPriorityActions} high priority actions requiring immediate attention and ${summary.mediumPriorityActions} medium priority items for investigation. `;
    } else {
      summaryText += "All recommendations are currently showing OK status with no immediate actions required. ";
    }

    // Add category breakdown
    const categoryEntries = Object.entries(summary.categoryBreakdown);
    if (categoryEntries.length > 0) {
      summaryText += "Recommendations are distributed across categories including: ";
      const categoryText = categoryEntries
        .sort(([,a], [,b]) => b - a) // Sort by count descending
        .map(([category, count]) => `${category} (${count})`)
        .join(', ');
      summaryText += categoryText + '. ';
    }

    // Add cost savings information
    if (summary.potentialMonthlySavings.contributingRecommendations > 0) {
      summaryText += `Cost optimization recommendations could potentially save ${summary.potentialMonthlySavings.formattedAmount} monthly, with ${summary.potentialMonthlySavings.contributingRecommendations} recommendations contributing to these savings.`;
    }

    children.push(
      new Paragraph({
        children: [new TextRun({ text: summaryText, size: 22 })],
        spacing: { after: 200 }
      })
    );

    // Action Items if there are any high or medium priority items
    if (summary.highPriorityActions > 0 || summary.mediumPriorityActions > 0) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: "Recommended Actions",
              bold: true,
              size: 18,
              underline: { type: UnderlineType.SINGLE }
            })
          ],
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 200, after: 100 }
        })
      );

      if (summary.highPriorityActions > 0) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "High Priority Actions",
                bold: true,
                size: 16,
                color: "C5504B"
              })
            ],
            spacing: { before: 100, after: 50 }
          })
        );

        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `• Review and address ${summary.highPriorityActions} recommendations marked as "Action Recommended"`,
                size: 22
              })
            ],
            spacing: { after: 50 }
          })
        );

        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "• These recommendations identify critical issues that should be resolved immediately to optimize security, performance, or cost",
                size: 22
              })
            ],
            spacing: { after: 100 }
          })
        );
      }

      if (summary.mediumPriorityActions > 0) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "Medium Priority Actions",
                bold: true,
                size: 16,
                color: "C55A11"
              })
            ],
            spacing: { before: 100, after: 50 }
          })
        );

        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `• Investigate ${summary.mediumPriorityActions} recommendations marked as "Investigation Recommended"`,
                size: 22
              })
            ],
            spacing: { after: 50 }
          })
        );

        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "• These recommendations may require further analysis to determine if action is needed",
                size: 22
              })
            ],
            spacing: { after: 100 }
          })
        );
      }

      if (summary.potentialMonthlySavings.contributingRecommendations > 0) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "Cost Optimization Priority",
                bold: true,
                size: 16,
                color: "70AD47"
              })
            ],
            spacing: { before: 100, after: 50 }
          })
        );

        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `• Focus on cost optimization recommendations to achieve potential monthly savings of ${summary.potentialMonthlySavings.formattedAmount}`,
                size: 22
              })
            ],
            spacing: { after: 50 }
          })
        );

        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "• Detailed recommendations and affected resources can be found in the Excel export for implementation planning",
                size: 22
              })
            ],
            spacing: { after: 200 }
          })
        );
      }
    }

    return children;
  }

  // Create recommendations section
  createRecommendationsSection(recommendations) {
    const children = [];

    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "Action Items & Recommendations",
            bold: true,
            size: 20,
            underline: { type: UnderlineType.SINGLE },
            color: "2F5496"
          })
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 200, after: 100 }
      })
    );

    // Group by priority
    const groupedRecommendations = {
      high: recommendations.filter(r => r.priority === 'high'),
      medium: recommendations.filter(r => r.priority === 'medium'),
      low: recommendations.filter(r => r.priority === 'low')
    };

    ['high', 'medium', 'low'].forEach(priority => {
      if (groupedRecommendations[priority].length > 0) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `${priority.charAt(0).toUpperCase() + priority.slice(1)} Priority`,
                bold: true,
                size: 18,
                color: priority === 'high' ? 'C5504B' : priority === 'medium' ? 'C55A11' : '70AD47'
              })
            ],
            spacing: { before: 200, after: 100 }
          })
        );

        groupedRecommendations[priority].forEach((rec, index) => {
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `• ${rec.action}`,
                  bold: true
                })
              ],
              spacing: { before: 100, after: 50 }
            })
          );

          if (rec.dataJustification) {
            children.push(
              new Paragraph({
                children: [
                  new TextRun({ text: `  Data Justification: ${rec.dataJustification}` })
                ],
                spacing: { after: 50 }
              })
            );
          }

          if (rec.affectedCount) {
            children.push(
              new Paragraph({
                children: [
                  new TextRun({ text: `  Affected Functions: ${rec.affectedCount}`, italics: true })
                ],
                spacing: { after: 100 }
              })
            );
          }


        });
      }
    });

    return children;
  }

  // Create error section
  createErrorSection(errors) {
    const children = [];

    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "Analysis Errors",
            bold: true,
            size: 20,
            underline: { type: UnderlineType.SINGLE },
            color: "C5504B"
          })
        ],
        spacing: { before: 400, after: 100 }
      })
    );

    errors.forEach((error, index) => {
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `${index + 1}. ${error}`,
              color: "C5504B"
            })
          ],
          spacing: { after: 100 }
        })
      );
    });

    return children;
  }

  // Generate and download document
  async generateDocument(analysisResults, filename) {
    try {
      const wordDocument = await this.createAnalysisDocument(analysisResults);
      
      // Generate blob
      const blob = await Packer.toBlob(wordDocument);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error('Error generating document:', error);
      throw error;
    }
  }

  // Helper methods
  getServiceDisplayName(serviceName) {
    const displayNames = {
      lambda: 'AWS Lambda Functions',
      lexV2: 'Amazon Lex V2 Bots',
      connect: 'Amazon Connect',
      trustedAdvisor: 'AWS Trusted Advisor'
    };
    return displayNames[serviceName] || serviceName;
  }

  formatMetricKey(key) {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }
}

// Export singleton instance
export const wordDocumentService = new WordDocumentService();